/**
 * Repair / Doctor page.
 *
 * - Calls GET /api/repair/checklist on mount.
 * - For each FAIL/WARN check with a fixId, shows a fix action.
 * - Support bundle creation remains PIN-gated.
 */
import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CircleCheckBig, ShieldAlert, Wrench } from 'lucide-react';
import { api } from '../api';
import { AppShell } from '../ui/AppShell';
import { PageHeader } from '../ui/PageHeader';
import { SectionCard } from '../ui/components';
import { ActionQueueCard, MetricStrip } from '../ui/mobileOps';
import { Button, EmptyState, ErrorBanner, Pill, Spinner } from '../ui/primitives';
import { usePinGate } from '../auth/pinGate';

type CheckStatus = 'pass' | 'warn' | 'fail' | 'skip';

interface CheckRecord {
  id: string;
  title: string;
  status: CheckStatus;
  detail: string;
  fixId: string | null;
  destructive: boolean;
}

interface ChecklistResponse {
  generatedAt: string;
  port: number;
  dataDir: string;
  checks: CheckRecord[];
  summary: { pass: number; warn: number; fail: number; skip: number };
}

const STATUS_LABELS: Record<CheckStatus, string> = {
  pass: 'Pass',
  warn: 'Warn',
  fail: 'Fail',
  skip: 'Skip',
};

const STATUS_TONES: Record<CheckStatus, 'green' | 'amber' | 'red' | 'neutral'> = {
  pass: 'green',
  warn: 'amber',
  fail: 'red',
  skip: 'neutral',
};

export function RepairPage() {
  const qc = useQueryClient();
  const { withPin } = usePinGate();
  const [busyFix, setBusyFix] = useState<string | null>(null);
  const [bundleBusy, setBundleBusy] = useState(false);
  const [description, setDescription] = useState('');
  const [bundlePath, setBundlePath] = useState<string | null>(null);
  const [bundleErr, setBundleErr] = useState<string | null>(null);

  const checklist = useQuery<ChecklistResponse>({
    queryKey: ['repair-checklist'],
    queryFn: () => api.get('/api/repair/checklist'),
    refetchOnMount: true,
    staleTime: 0,
  });

  const runFix = async (fixId: string): Promise<void> => {
    setBusyFix(fixId);
    try {
      await withPin(async () => {
        await api.post<{ ok: boolean; result: unknown }>('/api/repair/fix', { fixId });
      });
    } finally {
      setBusyFix(null);
      void qc.invalidateQueries({ queryKey: ['repair-checklist'] });
    }
  };

  const runBundle = async (): Promise<void> => {
    setBundleBusy(true);
    setBundleErr(null);
    try {
      await withPin(async () => {
        const res = await api.post<{
          ok: boolean;
          result: { bundle?: string } | null;
        }>('/api/repair/support-bundle', { description });
        setBundlePath(res?.result?.bundle ?? null);
      });
    } catch (err) {
      setBundlePath(null);
      setBundleErr(err instanceof Error ? err.message : 'unknown');
    } finally {
      setBundleBusy(false);
    }
  };

  const toolbar = (
    <PageHeader
      section="settings"
      title="Repair"
      subtitle="Diagnostics, guided fixes, and support bundle generation."
      breadcrumb={[{ label: 'Settings', to: '/settings' }, { label: 'Repair' }]}
    />
  );

  if (checklist.isLoading) {
    return (
      <AppShell title="Repair" toolbar={toolbar}>
        <SectionCard section="settings" title="Running checklist">
          <div className="flex items-center gap-3 text-small text-ink-muted">
            <Spinner /> Running diagnostics...
          </div>
        </SectionCard>
      </AppShell>
    );
  }

  if (checklist.error || !checklist.data) {
    return (
      <AppShell title="Repair" toolbar={toolbar}>
        <div className="mx-auto max-w-4xl space-y-4">
          <ActionQueueCard
            section="settings"
            eyebrow="Unavailable"
            title="Repair engine is not available in this runtime"
            description={(checklist.error as Error)?.message ?? 'The checklist could not be loaded.'}
            tone="warn"
            meta={
              <div className="text-[12px] leading-5 text-ink-muted">
                On this deployment, the backend repair engine may be disabled or unsupported. If support asks for diagnostics, generate them from the environment where the server process actually runs.
              </div>
            }
          />
        </div>
      </AppShell>
    );
  }

  const data = checklist.data;
  const allOk = data.summary.fail === 0 && data.summary.warn === 0;
  const tunnelCheck = data.checks.find((check) => check.id === 'tunnel-alive') ?? null;

  return (
    <AppShell title="Repair" toolbar={toolbar}>
      <div className="mx-auto max-w-5xl space-y-4">
        <MetricStrip
          section="settings"
          columns={4}
          items={[
            { label: 'Pass', value: String(data.summary.pass), tone: 'pos' },
            { label: 'Warn', value: String(data.summary.warn), tone: data.summary.warn > 0 ? 'warn' : 'default' },
            { label: 'Fail', value: String(data.summary.fail), tone: data.summary.fail > 0 ? 'neg' : 'default' },
            { label: 'Skip', value: String(data.summary.skip), note: 'not run in this environment' },
          ]}
        />

        <div className="grid gap-4 lg:grid-cols-[1.04fr_0.96fr]">
          <ActionQueueCard
            section="settings"
            eyebrow="Repair desk"
            title={allOk ? 'Everything currently looks healthy' : 'Review the flagged checks before forcing changes'}
            description={allOk ? 'The latest checklist did not report any warnings or failures.' : 'Guided fixes stay PIN-gated. Destructive repairs trigger a safety backup first when the backend supports it.'}
            tone={allOk ? 'accent' : 'warn'}
            meta={
              <div className="space-y-2">
                <div className="flex items-center justify-between gap-3">
                  <span>Generated</span>
                  <span className="font-medium text-ink">{new Date(data.generatedAt).toLocaleString()}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Port</span>
                  <span className="font-medium text-ink">{data.port}</span>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <span>Data directory</span>
                  <span className="truncate font-mono text-[12px] text-ink">{data.dataDir}</span>
                </div>
              </div>
            }
          />

          <SectionCard section="settings" title="Connectivity">
            {tunnelCheck && tunnelCheck.status !== 'skip' ? (
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Pill tone={STATUS_TONES[tunnelCheck.status]}>{STATUS_LABELS[tunnelCheck.status]}</Pill>
                  <span className="text-small font-medium text-ink">Tunnel / public session</span>
                </div>
                <div className="text-[13px] leading-5 text-ink-muted">{tunnelCheck.detail}</div>
              </div>
            ) : (
              <EmptyState
                section="settings"
                icon={ShieldAlert}
                title="No tunnel check in this environment"
                description="That is normal on deployments where the app already sits behind a server or reverse proxy."
              />
            )}
          </SectionCard>
        </div>

        <SectionCard section="settings" title="Checklist">
          <div className="space-y-3">
            {data.checks.map((check) => (
              <ChecklistCard
                key={check.id}
                check={check}
                fixing={busyFix === check.fixId}
                onFix={check.fixId ? () => void runFix(check.fixId as string) : undefined}
              />
            ))}
          </div>
        </SectionCard>

        <SectionCard section="settings" title="Support bundle">
          <p className="mb-3 text-[13px] leading-5 text-ink-muted">
            Add a short description, then generate a scrubbed bundle for support. Passwords, PINs, ledger bodies, and attachments are excluded.
          </p>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value.slice(0, 500))}
            placeholder="What went wrong? What were you trying to do?"
            className="min-h-[120px] w-full rounded-[18px] border border-border/70 bg-surface px-3.5 py-3 text-body text-ink shadow-sm backdrop-blur placeholder:text-ink-muted focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
            rows={4}
          />
          <div className="mt-2 text-right text-caption text-ink-muted">{description.length} / 500</div>
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Button type="button" onClick={() => void runBundle()} disabled={bundleBusy}>
              {bundleBusy ? <Spinner /> : 'Generate support bundle'}
            </Button>
            {bundlePath && <span className="text-caption text-pos">Saved: {bundlePath}</span>}
          </div>
          {bundleErr && <div className="mt-3"><ErrorBanner>Couldn't generate bundle: {bundleErr}</ErrorBanner></div>}
        </SectionCard>
      </div>
    </AppShell>
  );
}

function ChecklistCard({
  check,
  fixing,
  onFix,
}: {
  check: CheckRecord;
  fixing: boolean;
  onFix?: () => void;
}) {
  const canFix = check.fixId && (check.status === 'fail' || check.status === 'warn');
  const statusTone = STATUS_TONES[check.status];
  const statusLabel = STATUS_LABELS[check.status];
  const toneClass =
    check.status === 'fail'
      ? 'border-red-200 bg-red-50/88'
      : check.status === 'warn'
      ? 'border-amber-200 bg-amber-50/88'
      : 'border-border/70 app-shell-surface';

  return (
    <div className={`rounded-[24px] border p-4 shadow-sm backdrop-blur ${toneClass}`}>
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Pill tone={statusTone}>{statusLabel}</Pill>
            {check.destructive && <Pill tone="red">Backup first</Pill>}
          </div>
          <div className="mt-2 text-small font-semibold tracking-[-0.01em] text-ink">{check.title}</div>
          <div className="mt-1 text-[13px] leading-5 text-ink-muted">{check.detail}</div>
        </div>
        {canFix && onFix && (
          <Button type="button" variant="secondary" size="sm" onClick={onFix} disabled={fixing}>
            {fixing ? <Spinner /> : 'Run fix'}
          </Button>
        )}
      </div>
    </div>
  );
}
